/**
 * Workspace setup — shared between login and boot paths.
 *
 * Handles three cases in order:
 *  1. Workspace ID already in localStorage → re-establishes context (reload / re-login)
 *  2. No local ID but server has one → adopts the server workspace (new device joining)
 *  3. No workspace anywhere → creates fresh personal workspace (first-ever setup)
 */
import { getProfiles, getActiveProfileId, getAccount } from '@yada/storage';
import { listWorkspaces, createWorkspace, getWorkspace } from '@yada/api-client';
import { createWorkspaceSeed } from '@yada/sync';
import { addActiveWorkspace, setCurrentWorkspaceId } from './workspaceContext';

export async function ensurePersonalWorkspace(): Promise<void> {
  try {
    const profiles = await getProfiles();
    const activeProfileId = await getActiveProfileId();
    let activeProfile = activeProfileId ? profiles.find(p => p.id === activeProfileId) : null;

    if (!activeProfile) {
      activeProfile =
        profiles.find(p => p.profileType === 'standard') ||
        profiles.find(p => p.profileType === 'internal') ||
        profiles[0] ||
        null;
    }

    if (!activeProfile) {
      console.warn('[workspaceSetup] No profile found, cannot set up workspace');
      return;
    }

    const currentAccount = await getAccount();
    const isAccountless = currentAccount?.isAccountless;

    const storedWorkspaceId = localStorage.getItem('yada:activeWorkspaceId');

    if (storedWorkspaceId) {
      // Case 1: Re-establish context for a workspace already known to this device
      const workspaceName = 'Personal Workspace';
      addActiveWorkspace({
        workspaceId: storedWorkspaceId,
        profileId: activeProfile.id,
        authState: 'authenticated',
        profileName: activeProfile.name,
        workspaceName,
      });
      setCurrentWorkspaceId(storedWorkspaceId);
      console.log('[workspaceSetup] Re-established workspace context:', storedWorkspaceId);

      // Verify/update workspace details from server
      if (!isAccountless && typeof navigator !== 'undefined' && navigator.onLine !== false) {
        try {
          const workspace = await getWorkspace(storedWorkspaceId);
          if (workspace.name) {
            addActiveWorkspace({
              workspaceId: storedWorkspaceId,
              profileId: activeProfile.id,
              authState: 'authenticated',
              profileName: activeProfile.name,
              workspaceName: workspace.name,
            });
          }
          if (workspace.storage_url && !localStorage.getItem('yada:defaultStorageUrl')) {
            localStorage.setItem('yada:defaultStorageUrl', workspace.storage_url);
            console.log('[workspaceSetup] Restored storage URL:', workspace.storage_url);
          }
        } catch (err) {
          console.debug('[workspaceSetup] Workspace check skipped:', err);
        }
      }
      return;
    }

    // Case 2: No local workspace ID — check server before creating anything.
    if (!isAccountless) {
      let serverWorkspaceId: string | null = null;
      let serverWorkspaceName = 'Personal Workspace';
      let serverStorageUrl: string | undefined;

      try {
        const workspaces = await listWorkspaces();
        const personal =
          workspaces.find(w => w.workspace_type === 'personal' && w.membership_status === 'active') ||
          workspaces.find(w => w.workspace_type === 'group' && w.name === 'Personal Workspace' && w.membership_status === 'active') ||
          workspaces.find(w => w.workspace_type !== 'dm' && w.membership_status === 'active');
        if (personal) {
          serverWorkspaceId = personal.id;
          serverWorkspaceName = personal.name || serverWorkspaceName;
          serverStorageUrl = personal.storage_url;
        }
      } catch (err) {
        console.warn('[workspaceSetup] Failed to list workspaces from server:', err);
      }

      if (serverWorkspaceId) {
        localStorage.setItem('yada:activeWorkspaceId', serverWorkspaceId);
        if (serverStorageUrl && !localStorage.getItem('yada:defaultStorageUrl')) {
          localStorage.setItem('yada:defaultStorageUrl', serverStorageUrl);
        }
        addActiveWorkspace({
          workspaceId: serverWorkspaceId,
          profileId: activeProfile.id,
          authState: 'authenticated',
          profileName: activeProfile.name,
          workspaceName: serverWorkspaceName,
        });
        setCurrentWorkspaceId(serverWorkspaceId);
        console.log('[workspaceSetup] Adopted existing server workspace:', serverWorkspaceId);
        return;
      }
    }

    // Case 3: No workspace on server either — first-ever setup, create one.
    let workspaceId: string;
    let workspaceName = 'Personal Workspace';

    if (isAccountless) {
      workspaceId = crypto.randomUUID();
      console.log('[workspaceSetup] Creating local-only workspace for accountless account:', workspaceId);
    } else {
      const storageUrl = localStorage.getItem('yada:defaultStorageUrl') || '';
      const workspace = await createWorkspace({
        name: workspaceName,
        storage_url: storageUrl,
        workspace_type: 'personal',
      });
      workspaceId = workspace.id;
      workspaceName = workspace.name;
    }

    await createWorkspaceSeed(workspaceId, activeProfile.id);
    localStorage.setItem('yada:activeWorkspaceId', workspaceId);
    addActiveWorkspace({
      workspaceId: workspaceId,
      profileId: activeProfile.id,
      authState: 'authenticated',
      profileName: activeProfile.name,
      workspaceName: workspaceName,
    });
    setCurrentWorkspaceId(workspaceId);
    console.log('[workspaceSetup] Created new workspace:', workspaceId);
  } catch (err) {
    console.error('[workspaceSetup] Setup failed:', err);
  }
}
